/**
 * REFERENCE TABLE — a mono identifier column beside a SENTENCE-CASE description column.
 *
 * LOCAL TO /pipeline FOR NOW. Listed in the design contract as a shared primitive (/help is
 * the next consumer), but src/components/primitives.tsx belongs to a concurrent workstream.
 * Flagged for consolidation; the API below is already the contract's.
 *
 * THIS FIXES A PRECISELY INVERTED TYPOGRAPHY. The old screen set whole English sentences in
 * UPPERCASE MONO with caps tracking — "DETERMINISTIC FACT COMPARISON", "BEFORE AND AFTER PAGE
 * IMAGES", "REVIEWER AND JUDGE VERDICTS" — while the real identifiers (final.pdf,
 * protected-facts-report.json) got sans 13px semibold. Backwards on both counts. The filename
 * IS the identifier and belongs in mono; the description is prose and belongs in sentence
 * case. Twelve shouted sentences: label monoculture in its purest form. There is no
 * text-transform on this screen at all.
 *
 * NO CHIPS. Identifiers render as plain mono text, never on a --code-bg ground. The sealed-chip
 * treatment is reserved for protected tokens in DocumentDiff; reusing it decoratively here
 * would dilute the one place in the product where it carries meaning.
 *
 * THREE TRACK WIDTHS, and the difference is a MEASUREMENT of the widest id the table holds,
 * not a taste. This is the whole point of the prop: an id column is exactly as wide as its
 * longest identifier and not one pixel wider, because every pixel past that is dead gutter
 * between a label and the sentence it belongs to.
 *
 *   'narrow'  — V-codes. Two columns from 390px up. The id track is --space-7 (48px), which
 *               comfortably fits "V14a" at 13px mono, so ALL 21 descriptions share ONE left
 *               edge on a phone. That is the alignment win, by construction.
 *
 *   'compact' — phase ids. "Phase 0".."Phase 8" is SEVEN mono characters (~55px at 13px), so
 *               the track is --space-8 (64px) and the description sits one --space-3 gap from
 *               it. It previously borrowed 'wide' — a 280px track measured for a 33-character
 *               FILENAME — and every one of the nine phase rows then showed ~225px of dead
 *               gutter between "Phase 3" and its sentence, so the pair read as two unrelated
 *               columns instead of one row. A track is sized by the content IN it.
 *
 *   'wide'    — filenames. STACKED on phone: "audience-personalization-memo.pdf" is 33
 *               unbreakable mono characters and cannot share a 390px row with a sentence —
 *               forcing it would either clip the filename or crush the description into a
 *               four-word column. It un-stacks into two columns when its PANEL clears 640px,
 *               where --list-col-min (280px) fits it with room.
 *
 * EVERY TRACK IS minmax(0, 1fr) WITH min-width: 0 AND overflow-wrap: anywhere. Never a plain
 * `1fr`. A grid track's implicit minimum is `auto` — its widest item's min-content — so ONE
 * unbreakable token sizes the whole column. That is not hypothetical: it is the exact bug that
 * threw the review surface ~900px off the side of a 390px screen in F3, and this table holds
 * precisely the hostile inputs (that 33-char filename, the 300-character V18 description).
 */
export function ReferenceTable({
  items,
  idWidth,
  variant = "plain",
  label,
  testId,
  rowTestId
}: {
  items: Array<{ id: string; description: string }>;
  /** The id track, sized to the longest identifier this table actually holds. See above. */
  idWidth: "narrow" | "compact" | "wide";
  /**
   * 'chip' sets the identifier as a CODE CHIP — mono on --accent-soft with --accent-ink text
   * (7.06:1), one fixed width so V1 and V14a share an edge — and lets the dl reflow into a
   * dense two-up reference grid when its PANEL is wide enough (a container query, so it stays
   * honestly one-up in a rail). It turns 21 validator ids from an unstyled <dl> into an
   * instrument. It is a variant, not a fork: same markup, same testids, same margin resets.
   *
   * This is the only blue on /pipeline. Because the screen renders no run, no colour here can
   * ever be mistaken for a verdict — which is exactly why it is safe to spend it on legibility.
   */
  variant?: "plain" | "chip";
  label?: string;
  testId?: string;
  rowTestId?: string;
}) {
  return (
    <dl
      className="reference-table"
      data-width={idWidth}
      data-variant={variant}
      data-testid={testId}
      aria-label={label}
    >
      {items.map((item) => (
        <div className="reference-row" key={item.id} data-testid={rowTestId} data-ref-id={item.id}>
          <dt className="reference-id">{item.id}</dt>
          <dd className="reference-description">{item.description}</dd>
        </div>
      ))}
    </dl>
  );
}
